import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import SubscriptionCancellationModal from './SubscriptionCancellationModal';
import PaymentMethodManagement from './PaymentMethodManagement';
import PaymentFailureWarning from './PaymentFailureWarning';
import { pushEvent, ANALYTICS_EVENTS } from '../../../utils/analytics';
import useUser from '../../../hooks/useUser';
import { usePaymentStatus } from '../../../contexts/PaymentStatusContext';
import type { PaymentFailureStatus, SubscriptionRecord } from '../../../types/account';
import { getErrorMessage } from '../../../utils/errorMessage';
import { Alert, Button } from '../../../design-system';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import UpgradeOfferSummary from '../../../components/UpgradeOfferSummary';
import type { UpgradeOffer } from '../../../hooks/useUpgradeOffer';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  readThroughAccountSettingsCache,
} from '../accountSettingsDataCache';
import './SubscriptionManagement.css';

interface SubscriptionsResponse {
  subscriptions?: SubscriptionRecord[];
  pausedSubscriptions?: SubscriptionRecord[];
  pastSubscriptions?: SubscriptionRecord[];
  message?: string;
  upgradeOffer?: UpgradeOffer | null;
}

interface PaymentFailureStatusResponse {
  success?: boolean;
  paymentStatus?: PaymentFailureStatus | null;
}

interface SubscriptionManagementData {
  subscriptions: SubscriptionRecord[];
  pausedSubscriptions: SubscriptionRecord[];
  pastSubscriptions: SubscriptionRecord[];
  upgradeOffer?: UpgradeOffer | null;
}

interface PaymentFailureStatusData {
  paymentFailureStatus: PaymentFailureStatus | null;
}

interface CancellationResult {
  success?: boolean;
  subscriptionId?: string;
  outcome?: string;
  retentionOfferAccepted?: boolean;
}

type SubscriptionVariant = 'active' | 'paused';

const SubscriptionManagement = () => {
  const { getApiToken, isAuthenticated, authLoading, user } = useUser();
  const { refreshPaymentStatus } = usePaymentStatus();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'api';
  const userCacheKey = getAccountSettingsUserCacheKey(user);
  const subscriptionsCacheKey = makeAccountSettingsCacheKey('subscriptions', userCacheKey, apiBaseUrl);
  const paymentFailureCacheKey = makeAccountSettingsCacheKey('subscription-payment-failure', userCacheKey, apiBaseUrl);
  const cachedSubscriptionData = readAccountSettingsCache<SubscriptionManagementData>(subscriptionsCacheKey);
  const cachedPaymentFailureData = readAccountSettingsCache<PaymentFailureStatusData>(paymentFailureCacheKey);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>(() => cachedSubscriptionData?.subscriptions || []);
  const [pausedSubscriptions, setPausedSubscriptions] = useState<SubscriptionRecord[]>(() => cachedSubscriptionData?.pausedSubscriptions || []);
  const [pastSubscriptions, setPastSubscriptions] = useState<SubscriptionRecord[]>(() => cachedSubscriptionData?.pastSubscriptions || []);
  const [upgradeOffer, setUpgradeOffer] = useState<UpgradeOffer | null>(
    () => cachedSubscriptionData?.upgradeOffer || null,
  );
  const [showPastSubscriptions, setShowPastSubscriptions] = useState(false);
  const [loading, setLoading] = useState(() => !cachedSubscriptionData);
  const [error, setError] = useState('');
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionRecord | null>(null);

  const [paymentFailureStatus, setPaymentFailureStatus] = useState<PaymentFailureStatus | null>(
    () => cachedPaymentFailureData?.paymentFailureStatus || null,
  );
  const [paymentFailureLoading, setPaymentFailureLoading] = useState(false);

  const activeSubscriptionCountRef = useRef<number | null>(null);

  const applySubscriptionData = useCallback((
    data: SubscriptionManagementData,
    options: { refreshOnCountChange?: boolean } = {},
  ): void => {
    const nextActiveCount = data.subscriptions.filter((sub) => sub.status === 'active').length;
    const previousActiveCount = activeSubscriptionCountRef.current;

    setSubscriptions(data.subscriptions);
    setPausedSubscriptions(data.pausedSubscriptions);
    setPastSubscriptions(data.pastSubscriptions);
    setUpgradeOffer(data.upgradeOffer || null);
    activeSubscriptionCountRef.current = nextActiveCount;

    if (options.refreshOnCountChange && previousActiveCount !== null && previousActiveCount !== nextActiveCount) {
      Promise.resolve().then(refreshPaymentStatus).catch(() => undefined);
    }
  }, [refreshPaymentStatus]);

  const fetchSubscriptions = useCallback(async (options: { force?: boolean } = {}) => {
    const cached = readAccountSettingsCache<SubscriptionManagementData>(subscriptionsCacheKey);
    if (cached && !options.force) {
      applySubscriptionData(cached);
      setError('');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const data = await readThroughAccountSettingsCache<SubscriptionManagementData>(subscriptionsCacheKey, async () => {
        const token = await getApiToken();
        const response = await fetch(`${apiBaseUrl}/subscription-management/subscriptions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 404) {
          return {
            subscriptions: [],
            pausedSubscriptions: [],
            pastSubscriptions: [],
            upgradeOffer: null,
          };
        }

        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({})) as SubscriptionsResponse;
          throw new Error(errorData.message || `API Error: ${response.status}`);
        }

        const data = await response.json() as SubscriptionsResponse;
        return {
          subscriptions: data.subscriptions || [],
          pausedSubscriptions: data.pausedSubscriptions || [],
          pastSubscriptions: data.pastSubscriptions || [],
          upgradeOffer: data.upgradeOffer || null,
        };
      }, options);

      applySubscriptionData(data, { refreshOnCountChange: true });

      pushEvent({
        event: ANALYTICS_EVENTS.SUBSCRIPTION_MANAGEMENT_VIEWED,
        subscriptions_count: data.subscriptions.length,
        active_subscriptions: data.subscriptions.filter((s) => s.status === 'active').length,
        paused_subscriptions: data.pausedSubscriptions.length,
        past_subscriptions: data.pastSubscriptions.length
      });
    } catch (err) {
      const message = getErrorMessage(err, '');
      if (message.includes('login_required')) {
        setError('Please log in again to view your subscriptions.');
      } else if (message.includes('consent_required')) {
        setError('Additional authorization required. Please log in again.');
      } else if (message && !/network|failed to fetch/i.test(message)) {
        setError(message);
      } else {
        setError('Unable to load subscriptions. Please check your connection and try again.');
      }
      setSubscriptions([]);
      setPausedSubscriptions([]);
      setPastSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, applySubscriptionData, getApiToken, subscriptionsCacheKey]);

  const fetchPaymentFailureStatus = useCallback(async (options: { force?: boolean } = {}) => {
    const cached = readAccountSettingsCache<PaymentFailureStatusData>(paymentFailureCacheKey);
    if (cached && !options.force) {
      setPaymentFailureStatus(cached.paymentFailureStatus);
      setPaymentFailureLoading(false);
      return;
    }

    try {
      setPaymentFailureLoading(true);

      const data = await readThroughAccountSettingsCache<PaymentFailureStatusData>(paymentFailureCacheKey, async () => {
        const token = await getApiToken();
        const response = await fetch(`${apiBaseUrl}/subscription-management/payment-failure-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          return { paymentFailureStatus: null };
        }

        const data = await response.json() as PaymentFailureStatusResponse;
        return {
          paymentFailureStatus: data.success ? data.paymentStatus ?? null : null,
        };
      }, options);

      setPaymentFailureStatus(data.paymentFailureStatus);

      if (data.paymentFailureStatus?.hasActiveFailures) {
        pushEvent({
          event: ANALYTICS_EVENTS.SUBSCRIPTION_MANAGEMENT_VIEWED,
          action: 'payment_failure_detected',
          warning_level: data.paymentFailureStatus.warningLevel,
          failure_count: data.paymentFailureStatus.failureCount,
          services_suspended: data.paymentFailureStatus.servicesSuspended,
          total_amount_due: data.paymentFailureStatus.totalAmountDue
        });
      }
    } catch {
      setPaymentFailureStatus(null);
    } finally {
      setPaymentFailureLoading(false);
    }
  }, [apiBaseUrl, getApiToken, paymentFailureCacheKey]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      void fetchSubscriptions();
      void fetchPaymentFailureStatus();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
      setError('Please log in to view your subscriptions.');
    }
  }, [isAuthenticated, authLoading, fetchSubscriptions, fetchPaymentFailureStatus]);

  const handlePaymentWarningClick = () => {
    const activeSubscription = subscriptions.find(sub => ['active', 'trialing', 'past_due'].includes(sub.status));

    if (activeSubscription) {
      const paymentMethodElement = document.querySelector(`[data-subscription-id="${activeSubscription.id}"] .payment-method-section`);
      if (paymentMethodElement) {
        paymentMethodElement.scrollIntoView({ behavior: 'smooth' });
        paymentMethodElement.classList.add('highlight-payment-method');
        setTimeout(() => {
          paymentMethodElement.classList.remove('highlight-payment-method');
        }, 3000);
      }
    }
  };

  const handleCancelSubscription = (subscription: SubscriptionRecord): void => {
    setSelectedSubscription(subscription);

    pushEvent({
      event: ANALYTICS_EVENTS.SUBSCRIPTION_CANCELLATION_STARTED,
      subscription_id: subscription.id,
      plan_name: subscription.planName,
      subscription_age_days: subscription.subscriptionAgeDays,
      cancellation_flow: 'retention'
    });

    setShowCancellationModal(true);
  };

  const handleCancellationComplete = (result: unknown = {}): void => {
    const cancellationResult = result as CancellationResult;
    setShowCancellationModal(false);
    setSelectedSubscription(null);

    if (cancellationResult.success) {
      void fetchSubscriptions({ force: true });

      Promise.resolve().then(refreshPaymentStatus).catch(() => undefined);

      pushEvent({
        event: ANALYTICS_EVENTS.SUBSCRIPTION_CANCELLATION_COMPLETED,
        subscription_id: cancellationResult.subscriptionId,
        outcome: cancellationResult.outcome,
        retention_offer_accepted: cancellationResult.retentionOfferAccepted || false,
        cancellation_flow: 'retention'
      });
    }
  };

  const getStatusLabel = (status: string): ReactNode => {
    const statusConfig = {
      active: { tone: 'success', text: 'Active' },
      paused: { tone: 'warning', text: 'Paused' },
      canceled: { tone: 'muted', text: 'Cancelled' },
      past_due: { tone: 'danger', text: 'Past Due' },
      unpaid: { tone: 'danger', text: 'Unpaid' }
    } as Record<string, { tone: string; text: string }>;

    const config = statusConfig[status] || { tone: 'muted', text: status };
    return (
      <span className={`subscription-status subscription-status--${config.tone}`}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount?: number | string | null, currency = 'USD'): string => {
    const numericAmount = typeof amount === 'number' ? amount : Number(amount);
    if (amount === null || amount === undefined || Number.isNaN(numericAmount)) {
      return 'N/A';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(numericAmount / 100);
  };

  const formatSubscriptionAge = (subscription: SubscriptionRecord): string => {
    if (typeof subscription.subscriptionAgeDays === 'number') {
      return `${subscription.subscriptionAgeDays} days`;
    }

    if (!subscription.createdAt) {
      return 'N/A';
    }

    const createdAt = new Date(subscription.createdAt).getTime();
    if (Number.isNaN(createdAt)) {
      return 'N/A';
    }

    const days = Math.max(0, Math.floor((Date.now() - createdAt) / (1000 * 60 * 60 * 24)));
    return `${days} days`;
  };

  const renderCreditNotice = (subscription: SubscriptionRecord, variant: SubscriptionVariant = 'active'): ReactNode => {
    if (!subscription.hasFreeMonths) {
      return null;
    }

    return (
      <div className="subscription-credit-notice">
        <strong>You have {subscription.creditMonths} FREE month{subscription.creditMonths !== 1 ? 's' : ''}.</strong>
        <div>
          {subscription.creditSource === 'retention_benefit'
            ? 'Complimentary plan benefit'
            : `Credit Balance: ${formatCurrency(subscription.customerBalance)}`} ·{' '}
          {variant === 'paused'
            ? 'Benefits from feedback call'
            : `Next Charge: ${Number(subscription.nextChargeAmount) > 0 ? formatCurrency(subscription.nextChargeAmount) : '$0.00'}`}
        </div>
      </div>
    );
  };

  const renderBillingDate = (subscription: SubscriptionRecord): ReactNode => (
    <div className="detail-value">
      {subscription.nextBillingDate ? (
        <div>
          {subscription.hasFreeMonths ? (
            <div>
              <div className="subscription-credit-date">
                {formatDate(subscription.nextChargeDate || subscription.nextBillingDate)}
              </div>
              <div className="subscription-detail-note">
                {subscription.creditMonths} month{subscription.creditMonths !== 1 ? 's' : ''} of {subscription.creditSource === 'retention_benefit' ? 'complimentary access' : 'credit'} applied
              </div>
            </div>
          ) : (
            formatDate(subscription.nextBillingDate)
          )}
        </div>
      ) : 'N/A'}
    </div>
  );

  const renderSubscriptionDetails = (subscription: SubscriptionRecord): ReactNode => (
    <div className="subscription-detail-grid">
      <div className="detail-group">
        <div className="detail-label">
          {subscription.hasFreeMonths ? 'Regular Price' : 'Price'}
        </div>
        <div className="detail-value">
          {subscription.hasFreeMonths && (
            <span className="subscription-regular-price">
              {formatCurrency(subscription.amount, subscription.currency || 'USD')}
            </span>
          )}
          {subscription.hasFreeMonths ? (
            <span className="subscription-free-price">FREE</span>
          ) : (
            <>
              {formatCurrency(subscription.amount, subscription.currency || 'USD')}
              <small>/{subscription.interval}</small>
            </>
          )}
        </div>
      </div>

      <div className="detail-group">
        <div className="detail-label">
          {subscription.hasFreeMonths ? 'Next Charge Date' : 'Next Billing'}
          {subscription.hasFreeMonths && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {subscription.creditSource === 'retention_benefit'
                    ? `Your complimentary plan benefit covers ${subscription.creditMonths} month${subscription.creditMonths !== 1 ? 's' : ''}.`
                    : `Your account credit covers ${subscription.creditMonths} month${subscription.creditMonths !== 1 ? 's' : ''}.`}
                  {' '}You won't be charged until the benefit is used up.
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="subscription-credit-info-icon" />
            </OverlayTrigger>
          )}
        </div>
        {renderBillingDate(subscription)}
      </div>

      <div className="detail-group">
        <div className="detail-label">Started</div>
        <div className="detail-value">
          {formatDate(subscription.createdAt)}
        </div>
      </div>

      <div className="detail-group">
        <div className="detail-label">Customer Since</div>
        <div className="detail-value">
          {formatSubscriptionAge(subscription)}
        </div>
      </div>
    </div>
  );

  const renderSubscriptionRecord = (subscription: SubscriptionRecord, variant: SubscriptionVariant = 'active'): ReactNode => (
    <article
      className={`subscription-record ${variant === 'paused' ? 'subscription-record-paused' : ''}`}
      key={subscription.id}
      data-subscription-id={subscription.id}
    >
      <header className="subscription-record-header">
        <div className="plan-info">
          <div className="plan-details">
            <span className="plan-name">{subscription.planName}</span>
          </div>
          {getStatusLabel(subscription.status)}
        </div>
      </header>

      <div className="subscription-record-body">
        {renderCreditNotice(subscription, variant)}
        {renderSubscriptionDetails(subscription)}

        <div className="subscription-payment-method">
          <PaymentMethodManagement subscription={subscription} />
        </div>

        <div className="subscription-danger-row">
          <div className="subscription-danger-copy">
            <div className="subscription-danger-title">Plan controls</div>
            <p>Cancel active subscription.</p>
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleCancelSubscription(subscription)}
            disabled={variant === 'paused' && loading}
            className="subscription-cancel-button"
          >
            Cancel Subscription
          </Button>
        </div>
      </div>
    </article>
  );

  const renderPastSubscriptionRecord = (subscription: SubscriptionRecord): ReactNode => (
    <article className="subscription-record subscription-record-past" key={subscription.id}>
      <header className="subscription-record-header">
        <div className="plan-info">
          <div className="plan-details">
            <span className="plan-name">{subscription.planName}</span>
          </div>
          {getStatusLabel(subscription.status)}
        </div>
      </header>

      <div className="subscription-detail-grid">
        <div className="detail-group">
          <div className="detail-label">Started</div>
          <div className="detail-value">
            {formatDate(subscription.createdAt)}
          </div>
        </div>

        <div className="detail-group">
          <div className="detail-label">Status</div>
          <div className="detail-value">
            {subscription.status.replace('_', ' ')}
          </div>
        </div>

        {subscription.canceledAt && (
          <div className="detail-group">
            <div className="detail-label">Cancelled</div>
            <div className="detail-value">
              {subscription.canceledAt}
            </div>
          </div>
        )}

        {subscription.endedAt && (
          <div className="detail-group">
            <div className="detail-label">Ended</div>
            <div className="detail-value">
              {subscription.endedAt}
            </div>
          </div>
        )}
      </div>
    </article>
  );

  const goToPlans = (source: string): void => {
    pushEvent({
      event: ANALYTICS_EVENTS.SUBSCRIPTION_MANAGEMENT_VIEWED,
      action: 'upgrade_cta_clicked',
      source
    });
    window.location.href = '/pricing';
  };

  const goToUpgrade = (): void => {
    pushEvent({
      event: ANALYTICS_EVENTS.SUBSCRIPTION_MANAGEMENT_VIEWED,
      action: upgradeOffer?.terminal ? 'enterprise_interest_clicked' : 'upgrade_cta_clicked',
      source: 'catalog_upgrade_offer',
      product_slug: upgradeOffer?.productSlug || null,
    });
    window.location.href =
      upgradeOffer?.checkoutUrl || upgradeOffer?.contactSalesUrl || '/pricing';
  };

  const renderBillingAction = (
    description: string,
    actionLabel: string,
    onClick: () => void,
  ): ReactNode => (
    <section className="subscription-action-row" aria-label={actionLabel}>
      <div>
        <p>{description}</p>
      </div>
      <Button type="button" variant="outline-primary" onClick={onClick}>
        {actionLabel}
      </Button>
    </section>
  );

  const isInitialLoading = loading || authLoading;
  const hasCurrentSubscriptions = subscriptions.length > 0 || pausedSubscriptions.length > 0;

  return (
    <div className="subscription-management">
      <div className="settings-subpage-header subscription-header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Subscriptions & Billing</h2>
          <p className="settings-subpage-description subscription-description">
            Manage plan, payment method, and history.
          </p>
        </div>
      </div>

      {isInitialLoading ? (
        <AccountSettingsLoadingState
          className="subscription-management-loading"
          label="Loading subscriptions"
          text="Loading subscriptions..."
          variant="section"
        />
      ) : error ? (
        <Alert variant="danger" className="subscription-management-error">
          <span>{error}</span>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => fetchSubscriptions({ force: true })}
          >
            Retry
          </Button>
        </Alert>
      ) : (
        <>
          <PaymentFailureWarning
            paymentStatus={paymentFailureStatus}
            onUpdatePaymentClick={handlePaymentWarningClick}
            loading={paymentFailureLoading}
          />

          {!hasCurrentSubscriptions ? (
            <section className="subscription-empty-state">
              <div>
                <h3>No active subscriptions</h3>
              </div>
              <Button
                type="button"
                variant="primary"
                onClick={() => goToPlans('no_subscriptions_empty_state')}
              >
                View Plans
              </Button>
            </section>
          ) : (
            <>
              {subscriptions.length > 0 && (
                <section className="subscription-group" aria-label={`Active subscriptions (${subscriptions.length})`}>
                  <div className="subscription-record-list">
                    {subscriptions.map((subscription) => renderSubscriptionRecord(subscription, 'active'))}
                  </div>
                </section>
              )}

              {pausedSubscriptions.length > 0 && (
                <section className="subscription-group" aria-label={`Paused subscriptions (${pausedSubscriptions.length})`}>
                  <div className="subscription-record-list">
                    {pausedSubscriptions.map((subscription) => renderSubscriptionRecord(subscription, 'paused'))}
                  </div>
                </section>
              )}

              {upgradeOffer ? (
                <UpgradeOfferSummary offer={upgradeOffer} onUpgrade={goToUpgrade} />
              ) : (
                renderBillingAction(
                  'Compare available plans when you are ready to change your account.',
                  'View Plans',
                  () => goToPlans('inline_plan_action'),
                )
              )}
            </>
          )}
          {pastSubscriptions.length > 0 && (
            <section className="subscription-group">
              <div className="subscription-group-header subscription-group-header--toggle">
                <button
                  type="button"
                  className="subscription-history-toggle"
                  aria-expanded={showPastSubscriptions}
                  onClick={() => setShowPastSubscriptions(!showPastSubscriptions)}
                >
                  <span>{showPastSubscriptions ? 'Hide' : 'Show'} Past Subscriptions ({pastSubscriptions.length})</span>
                  <span aria-hidden="true">{showPastSubscriptions ? 'Collapse' : 'Expand'}</span>
                </button>
              </div>

              {showPastSubscriptions && (
                <div className="subscription-record-list">
                  {pastSubscriptions.map(renderPastSubscriptionRecord)}
                </div>
              )}
            </section>
          )}

          {showCancellationModal && selectedSubscription && (
            <SubscriptionCancellationModal
              show={showCancellationModal}
              onHide={() => setShowCancellationModal(false)}
              subscription={selectedSubscription}
              onComplete={handleCancellationComplete}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SubscriptionManagement;
